import type {
    IRpSurgeryDateDecline, IRpSwapFailureRate, IRpChainEndDeclineRatio, IRpNkrDonorReferralConversionRate,
    IRpMissedMatchOfferDeadline, IRpKflSuccessRate,
    IRpMicrositePatientReferralSuccessRate, IRpVoucherDisconnect, IRpMicrositeEnrollmentRate, IRpFiveStarSurveyResults,
    IRpNkrDonorReferralMedianContactTime, IRpSvDonations, IRpFvDonations
} from '@nkr/models/center-performance/relative-performance';
import { Utility } from '@nkr/services/utility';
import { PopoverWrapper } from '@nkr/views/admin/center/list/saturation-grid/overlays';
import { PopoverTableWrapper } from '@nkr/views/admin/center/list/styles';
import { OverlayProps } from 'react-bootstrap';

// Spread props here are the positional properties that OverlayTrigger passes down to the PopoverWrapper:
// (positionLeft, positionTop, style, etc.)
export const SurgeryDateDeclineOverlay = ({ surgeryDateDecline, ...props }: { surgeryDateDecline: IRpSurgeryDateDecline } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Decline Count</div>
                <div>Offer Count</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{surgeryDateDecline.declineCount}</div>
                <div>{surgeryDateDecline.offerCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const SwapFailureRateOverlay = ({ swapFailureRate, ...props }: { swapFailureRate: IRpSwapFailureRate } & OverlayProps) => {
    const adjustedFailures = Math.max(swapFailureRate.failures - swapFailureRate.covidFailures, 0);
    const totalSwaps = swapFailureRate.totalTransplants + adjustedFailures;

    return (
        <PopoverWrapper {...props}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                <div style={{ paddingRight: 10, textAlign: 'center' }}>
                    <div style={{ borderBottom: '1px solid black' }}>Total Failures</div>
                    <div>Total TXPs</div>
                </div>
                <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                    <div style={{ borderBottom: '1px solid black' }}>{adjustedFailures}</div>
                    <div>{totalSwaps}</div>
                </div>
            </div>
        </PopoverWrapper>
    );
};

export const ChainEndDeclineRatioOverlay = ({ chainEndDeclineRatio, ...props }: { chainEndDeclineRatio?: IRpChainEndDeclineRatio } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, marginBottom: 5 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Waitlist Offer Declines Last 6 Months (excluding internal one-way and AB offers)</div>
                <div>Waitlist Offers Last 6 Months (excluding internal one-way and AB offers)</div>
            </div>
            {chainEndDeclineRatio && <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{chainEndDeclineRatio.wlpDeclines >= 0 ? chainEndDeclineRatio.wlpDeclines : 0}</div>
                <div>{chainEndDeclineRatio.wlpOffers >= 0 ? chainEndDeclineRatio.wlpOffers : 0}</div>
            </div>}
        </div>
        <div>* Decline Ratio Threshold is 30%</div>
    </PopoverWrapper>
);

export const NkrDonorReferralMedianContactTimeOverlay = ({ nkrDonorReferralMedianContactTime, ...props }: { nkrDonorReferralMedianContactTime: IRpNkrDonorReferralMedianContactTime } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <PopoverTableWrapper>
            <tbody>
                <tr>
                    <td>Referrals last 12 months:</td>
                    <td className='text-right'>{Utility.roundNumber(nkrDonorReferralMedianContactTime.referralCount, 1)}</td>
                </tr>
            </tbody>
        </PopoverTableWrapper>
    </PopoverWrapper>
);

export const NkrDonorReferralConversionRateOverlay = ({ nkrDonorReferralConversionRate, ...props }: { nkrDonorReferralConversionRate: IRpNkrDonorReferralConversionRate } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Donated Count</div>
                <div>Referral Count</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{nkrDonorReferralConversionRate.donatedCount}</div>
                <div>{nkrDonorReferralConversionRate.referralCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const StandardVoucherDonationsOverlay = ({ svDonations, ...props }: { svDonations: IRpSvDonations } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>SVD Donations</div>
                <div>Total Donations</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{svDonations.totalStandardVouchers}</div>
                <div>{svDonations.totalDonations}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const FamilyVoucherDonationsOverlay = ({ fvDonations, ...props }: { fvDonations: IRpFvDonations } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>FVD Donations</div>
                <div>Total Donations</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{fvDonations.totalFamilyVouchers}</div>
                <div>{fvDonations.totalDonations}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const MissedMatchOfferDeadlineOverlay = ({ missedMatchOfferDeadline, ...props }: { missedMatchOfferDeadline: IRpMissedMatchOfferDeadline } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Missed Match Offer Deadlines</div>
                <div>Match Offers</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{missedMatchOfferDeadline.missedDeadlineCount}</div>
                <div>{missedMatchOfferDeadline.matchOfferCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const VoucherDisconnectOverlay = ({ voucherDisconnect, ...props }: { voucherDisconnect: IRpVoucherDisconnect } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Disconnects</div>
                <div>Total Donations</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{voucherDisconnect.disconnects}</div>
                <div>{voucherDisconnect.totalDonations}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const KflSuccessOverlay = ({ kflSuccessRate, ...props }: { kflSuccessRate: IRpKflSuccessRate } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Total Low and 0 MM TXPs</div>
                <div>Total TXPs w/ High Res Typing</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{kflSuccessRate.lowEpletTxpCount}</div>
                <div>{kflSuccessRate.txpRecipient3GTypingCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const MicrositePatientReferralSuccessRateOverlay = ({ micrositePatientReferralSuccessRate, ...props }: { micrositePatientReferralSuccessRate: IRpMicrositePatientReferralSuccessRate } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Microsites Created from Referrals (L 12 Mo.)</div>
                <div>Microsite Referrals (L 12 Mo.)</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{micrositePatientReferralSuccessRate.micrositeCount}</div>
                <div>{micrositePatientReferralSuccessRate.referralCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const MicrositeEnrollmentRateOverlay = ({ micrositeEnrollmentRate, ...props }: { micrositeEnrollmentRate: IRpMicrositeEnrollmentRate } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Active Microsites</div>
                <div>Total Waitlist Count</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{micrositeEnrollmentRate.activeMicrositeCount}</div>
                <div>{micrositeEnrollmentRate.waitlistCount}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const FiveStarSurveysOverlay = ({ fiveStarSurveyResults, ...props }: { fiveStarSurveyResults: IRpFiveStarSurveyResults } & OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div style={{ borderBottom: '1px solid black' }}>Sum of Survey Ratings</div>
                <div>Total Survey Responses</div>
            </div>
            <div style={{ paddingLeft: 10, paddingRight: 10, textAlign: 'center', backgroundColor: '#ddd', paddingTop: 5, paddingBottom: 5 }}>
                <div style={{ borderBottom: '1px solid black' }}>{Utility.roundNumber(fiveStarSurveyResults.scoreNumerator, 1)}</div>
                <div>{Utility.roundNumber(fiveStarSurveyResults.scoreDenominator, 1)}</div>
            </div>
        </div>
    </PopoverWrapper>
);

export const CompositeScoreOverlay = (props: OverlayProps) => (
    <PopoverWrapper {...props}>
        <div>FVD + SVD + Compatibles </div>
        <div>+ Remote Compatibles</div>
        <div>+ (Any Anat + 0 Decl bonus)</div>
        <div>* For Last 12 Months</div>
    </PopoverWrapper>
);

export const CompatiblePairRatioOverlay = (props: OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{display: 'grid', placeItems: 'center'}}>
            <div style={{ borderBottom: "1px solid black"}}>
                (Compatible, Paired & Voucher Transplants)
            </div>
            <div>
                Included Transplants
            </div>
        </div>
    </PopoverWrapper>
);

export const ThirtyDayPreselectOverlay = (props: OverlayProps) => (
    <PopoverWrapper {...props}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
                <div>Last 30 Day Preselect scores, ignoring lowest score</div>
                <div>* Exclude weekends</div>
            </div>
        </div>
    </PopoverWrapper>
);
